import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getAppleDeveloperToken } from '../../api/music';

export default function SoundSettingsPage() {
  const { user } = useAuth();

  const [appleMusicConnected, setAppleMusicConnected] = useState(false);
  const [appleMusicUser, setAppleMusicUser] = useState(null);
  const [musicKitLoading, setMusicKitLoading] = useState(false);
  const [musicError, setMusicError] = useState('');
  const musicKitRef = useRef(null);

  const appleEnabled = user?.music_integration_enabled && ['admin', 'trainer'].includes(user?.role);

  // Apple Music: MusicKit JS laden und ggf. bestehende Session wiederherstellen
  useEffect(() => {
    if (!appleEnabled) return;

    const existingToken = localStorage.getItem('apple_music_user_token');
    if (existingToken) {
      setAppleMusicConnected(true);
      setAppleMusicUser(localStorage.getItem('apple_music_storefront') || 'Verbunden');
    }

    if (window.MusicKit) return; // bereits geladen

    const script = document.createElement('script');
    script.src = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
    script.async = true;
    document.head.appendChild(script);
  }, [appleEnabled]);

  async function handleAppleMusicConnect() {
    setMusicKitLoading(true);
    setMusicError('');
    try {
      const { developer_token } = await getAppleDeveloperToken();

      await window.MusicKit.configure({
        developerToken: developer_token,
        app: { name: 'MK Workout', build: '1.0' },
      });

      const kit = window.MusicKit.getInstance();
      musicKitRef.current = kit;

      const userToken = await kit.authorize();
      const storefront = kit.storefrontId || 'Apple Music';

      localStorage.setItem('apple_music_user_token', userToken);
      localStorage.setItem('apple_music_storefront', storefront);

      setAppleMusicConnected(true);
      setAppleMusicUser(storefront);
    } catch (e) {
      setMusicError(e.message || 'Verbindung zu Apple Music fehlgeschlagen.');
    } finally {
      setMusicKitLoading(false);
    }
  }

  function handleAppleMusicDisconnect() {
    if (musicKitRef.current) {
      musicKitRef.current.unauthorize().catch(() => {});
    }
    localStorage.removeItem('apple_music_user_token');
    localStorage.removeItem('apple_music_storefront');
    setAppleMusicConnected(false);
    setAppleMusicUser(null);
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center pb-2 mb-4 border-bottom">
        <h1 className="h2">
          <i className="bi bi-music-note-beamed me-2" />
          Sound / Ton / Musik
        </h1>
      </div>

      <p className="text-muted">
        Musikdienste für die Wiedergabe im Presenter. Verbinde einen Streaming-Account, um Playlisten
        direkt während des Workouts abzuspielen.
      </p>

      {/* Apple Music */}
      <div className="card mb-4">
        <div className="card-header">
          <i className="bi bi-apple me-2" />
          Apple Music
        </div>
        <div className="card-body">
          {!appleEnabled ? (
            <div className="text-muted">
              <i className="bi bi-lock me-1" />
              Die Apple-Music-Integration ist für deinen Account nicht freigeschaltet.
            </div>
          ) : (
            <>
              {musicError && (
                <div className="alert alert-danger py-2">
                  <i className="bi bi-exclamation-triangle me-1" />
                  {musicError}
                </div>
              )}

              {appleMusicConnected ? (
                <div className="d-flex align-items-center gap-3">
                  <span className="badge bg-success fs-6 px-3 py-2">
                    <i className="bi bi-check-circle me-1" />
                    Verbunden ({appleMusicUser})
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleAppleMusicDisconnect}
                  >
                    <i className="bi bi-box-arrow-right me-1" />
                    Trennen
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-muted mb-3">
                    Verbinde deinen Apple Music Account, um Playlisten direkt im Presenter
                    abzuspielen. Dein Apple Music Abo ist erforderlich.
                  </p>
                  <button
                    type="button"
                    className="btn btn-dark"
                    onClick={handleAppleMusicConnect}
                    disabled={musicKitLoading}
                  >
                    {musicKitLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Verbinde…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-apple me-2" />
                        Apple Music verbinden
                      </>
                    )}
                  </button>
                </>
              )}

              <div className="form-text mt-2">
                Die Verbindung wird dauerhaft in diesem Browser gespeichert.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Spotify — Platzhalter (geplant) */}
      <div className="card mb-4 opacity-75">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>
            <i className="bi bi-spotify me-2" />
            Spotify
          </span>
          <span className="badge bg-secondary">Geplant</span>
        </div>
        <div className="card-body">
          <p className="text-muted mb-0">
            Die Spotify-Integration ist in Vorbereitung. Sobald sie verfügbar ist, kannst du hier
            deinen Spotify-Account verbinden und Playlisten im Presenter nutzen.
          </p>
        </div>
      </div>
    </div>
  );
}
